import { useState, useEffect, memo, useRef } from 'react'
import Box from '@mui/material/Box'
import Modal from '@mui/material/Modal'
import classes from './MailForm.module.scss'
import { getUserInfo } from '../../../../reducks/users/selectors'
import MailFormComponent from './components/MailFormComponent'
import { useSelector } from 'react-redux'
import ColorButtons from '../../ColorButtons'
import { ToastContainer } from 'react-toastify'
import 'react-toastify/dist/ReactToastify.css'

const style = {
  box: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    position: 'absolute',
    overflowY: 'auto',
    top: '50%',
    left: '50%',
    transform: 'translate(-50%, -50%)',
    width: {
      xs: '90%', // 0px以上の幅で適用
      sm: '80%', // 600px以上の幅で適用
    },
    height: {
      xs: '99%', // 0px以上の幅で適用
      sm: '99%', // 600px以上の幅で適用
    },
    bgcolor: 'background.paper',
    border: 'none',
    borderRadius: '10px',
    boxShadow: 24,
    padding: '10px 0 10px 0',
  },
}
const sleepTime = 500

const formatSentTime = (date) => {
  const hh = String(date.getHours()).padStart(2, '0')
  const mi = String(date.getMinutes()).padStart(2, '0')
  return `${hh}:${mi}`
}

// 送信済みメールを Gmail で直接開くための URL（送信アカウントを authuser で指定）
const gmailMessageUrl = (receipt, email) =>
  `https://mail.google.com/mail/?authuser=${encodeURIComponent(
    email
  )}#all/${receipt.threadId || receipt.messageId}`

export const MailForm = memo((props) => {
  const { orders, shops, setIsShow, setOrders, setShops } = props
  const [open, setOpen] = useState(true)
  // 仕入れ先ごとの送信状態: { [shop_id]: { status: 'sent' | 'skipped', receipt? } }
  // エントリなし = 未送信（フォーム表示中）
  const [shopStates, setShopStates] = useState({})
  const [renderedCount, setRenderedCount] = useState(0) // 現在表示している数
  const userInfo = useSelector(getUserInfo)
  const boxRef = useRef(null)

  const unsentShops = shops.filter(
    (shop) => shopStates[shop.shop_id]?.status !== 'sent'
  )
  const isAllSent = shops.length > 0 && unsentShops.length === 0

  const closeModal = () => {
    setIsShow(false)
    setOpen(false)
    setOrders([])
    setShops([])
  }

  // 未送信を残したまま閉じようとした場合は警告する
  // （ステータスは発注済へ更新済みのため、閉じてもメールは送信されない）
  const handleClose = () => {
    if (unsentShops.length > 0) {
      const ok = window.confirm(
        `未送信の仕入れ先が ${unsentShops.length} 件あります。\n` +
          '閉じてもステータスは発注済のままで、メールは送信されません。\n' +
          '（メールは後から「再発行」ボタンで送信できます）\n\n閉じますか？'
      )
      if (!ok) return
    }
    closeModal()
  }

  // 1秒ごとに1つずつレンダリングする処理（PDF生成の負荷分散）
  useEffect(() => {
    if (renderedCount < shops.length) {
      const timer = setTimeout(() => {
        setRenderedCount(renderedCount + 1)
      }, sleepTime)
      return () => clearTimeout(timer)
    }
  }, [renderedCount, shops.length])

  const handleSent = (shop_id, receipt) => {
    setShopStates((prev) => ({
      ...prev,
      [shop_id]: { status: 'sent', receipt },
    }))
    if (boxRef.current) {
      boxRef.current.scrollTo({
        top: 0,
        behavior: 'smooth',
      })
    }
  }

  const handleSkip = (shop_id) => {
    setShopStates((prev) => ({ ...prev, [shop_id]: { status: 'skipped' } }))
  }

  const handleResume = (shop_id) => {
    setShopStates((prev) => {
      const next = { ...prev }
      delete next[shop_id]
      return next
    })
  }

  return (
    <div>
      <Modal
        open={open}
        aria-labelledby="modal-modal-title"
        aria-describedby="modal-modal-description"
      >
        <Box sx={style.box} ref={boxRef}>
          <div className={classes.title}>
            メール送信 未送信:<span>{unsentShops.length}件</span>
          </div>
          {isAllSent && (
            <div className={classes.allSent}>
              すべての仕入れ先に送信しました
            </div>
          )}

          {/* renderedCount の数までのアイテムを表示 */}
          {shops.slice(0, renderedCount).map((shop, index) => {
            const state = shopStates[shop.shop_id]
            return (
              <div key={shop.shop_id} style={{ width: '100%' }}>
                <div className={classes.formContainer}>
                  <div className={classes.contentHeader}>
                    <p>{shop.shop_name}</p>
                    <p>
                      {index + 1}/{shops.length}件目
                    </p>
                  </div>
                  {state?.status === 'sent' ? (
                    <div className={classes.receipt}>
                      <span className={classes.receiptMark}>
                        ✓ 送信済み {formatSentTime(state.receipt.sentAt)}
                      </span>
                      {(state.receipt.threadId || state.receipt.messageId) && (
                        <a
                          href={gmailMessageUrl(state.receipt, userInfo.email)}
                          target="_blank"
                          rel="noopener noreferrer"
                        >
                          Gmailで確認
                        </a>
                      )}
                    </div>
                  ) : state?.status === 'skipped' ? (
                    <div className={classes.skipped}>
                      <p>
                        送信せずに省略しました（ステータスは発注済のままです）
                      </p>
                      <ColorButtons
                        label={'フォームを再表示'}
                        color="grey"
                        onClick={() => handleResume(shop.shop_id)}
                        width={160}
                        height={28}
                        bottom={0}
                      />
                    </div>
                  ) : (
                    <MailFormComponent
                      shop={shop}
                      orders={orders.filter(
                        (order) => order.shop_id === shop.shop_id
                      )}
                      onSent={handleSent}
                      onSkip={handleSkip}
                      userInfo={userInfo}
                    />
                  )}
                </div>
                <div className={classes.spacer}></div>
              </div>
            )
          })}

          <div className={classes.buttons}>
            <ColorButtons
              label={'閉じる'}
              color="grey"
              onClick={handleClose}
              width={200}
              height={32}
              bottom={0}
            />
          </div>
          <ToastContainer
            containerId="MailForm"
            position="top-left"
            autoClose={5000}
            hideProgressBar={false}
            newestOnTop={false}
            closeOnClick
            rtl={false}
            pauseOnFocusLoss
            draggable
            pauseOnHover
            theme="light"
          />
        </Box>
      </Modal>
    </div>
  )
})
